import { z } from 'zod';

import { reasoningEffortSchema } from './modelReasoningContract';
import { baseOrganizationEntitySchema } from './organizationContract';
import { builtinToolAllowlistSchema } from './tools';
import {
  Agent,
  ChatModel,
  ResponseLengthPreset,
  TonePreset,
  ChatPlacementPosition,
  ChatDisplayMode,
  ChatDomainPolicyMode,
  AgentType,
  Gender,
  ThemePreset,
  SourcesFormat,
  SourcesDisplayMode,
  GeminiVoices,
  GeminiLiveModel,
} from '../enums';
import { isValidWebsiteUrl, parseWebsiteUrlLines } from '../shared';

const websiteUrlLinesSchema = z.preprocess(
  parseWebsiteUrlLines,
  z
    .array(z.string().min(1))
    .optional()
    .superRefine((urls, ctx) => {
      if (urls?.some(url => !isValidWebsiteUrl(url))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid domain or URL',
        });
      }
    }),
);

export const chatThemeConfigSchema = z
  .object({
    preset: z.nativeEnum(ThemePreset).optional(),
    primary: z.string().nullish(),
    primaryFg: z.string().nullish(),
    logo: z.string().nullish(),
    logoBubble: z.string().nullish(),
  })
  .strict();

export type ChatThemeConfig = z.infer<typeof chatThemeConfigSchema>;

/**
 * Blend of a chunk's own vector and its document-summary vector for the
 * unrestricted retrieval query. Unset keeps `DEFAULT_VECTOR_WEIGHTS`
 * (content 0.7 / summary 0.3). Both halves are relative weights fused by
 * Weaviate's `relativeScore`, not probabilities — they need not sum to 1, but
 * a negative weight is meaningless.
 */
export const retrievalVectorWeightsSchema = z
  .object({
    content: z.number().nonnegative(),
    summary: z.number().nonnegative(),
  })
  .strict()
  .refine(weights => weights.content > 0 || weights.summary > 0, {
    message: 'At least one retrieval vector weight must be greater than zero',
  });

export type RetrievalVectorWeights = z.infer<
  typeof retrievalVectorWeightsSchema
>;

export const agentConfigSchema = z
  .object({
    /**
     * This field contains additional agent personalization like,
     * company description, name, it's role or additional directives
     */
    personalization: z.string().optional(),
    systemPrompt: z.string().optional(),
    gender: z.nativeEnum(Gender).optional(),
    hybridAlpha: z.number().min(0).max(1).optional(),
    /**
     * Dead since the agent runtime v2 rework: the v1 RAG graph's query
     * rewrite/retry loop these three drove no longer exists (retrieval is
     * model-driven now, no rewrite step; history is token-budgeted, not
     * message-count-limited). Kept here — not removed, not surfaced in the
     * admin UI — only so this `.strict()` schema still parses `agentConfig`
     * rows persisted before the rework (see `Migration20250102224755`'s
     * default, which set all three). Safe to drop once no stored config
     * still carries them.
     */
    rewriteRetries: z.number().int().nonnegative().optional(),
    historyLimit: z.number().int().nonnegative().optional(),
    contextHistoryLimit: z.number().int().nonnegative().optional(),
    historyTokenBudget: z.number().int().min(4000).max(200_000).optional(),
    toolResultHistoryTokenBudget: z
      .number()
      .int()
      .min(1000)
      .max(200_000)
      .optional(),
    retrievalCount: z.number().int().min(1).max(250).optional(),
    /**
     * Max tokens of a single documentSearch result fed back to the model.
     * Larger = the model sees more chunks in the live turn (better recall);
     * defaults to the runtime cap when unset (12 000, `middleware.ts`'s
     * `resolveToolResultCap`). Override-gated.
     */
    retrievalTokenCap: z.number().int().min(500).max(16_000).optional(),
    /**
     * Override-gated, like every other `retrival` knob: unset (or overrides
     * off) means `hybrid`, so this schema growing two keys changes nothing
     * for any existing chat.
     */
    retrievalVectorWeights: retrievalVectorWeightsSchema.optional(),
    rerankCount: z.number().int().min(1).max(100).optional(),
    weightRerankCoefficient: z.number().min(0).max(1).optional(),
    rerankCoefficient: z.number().min(0).max(1).optional(),
    rerank: z.boolean().optional(),
    imageSupport: z.boolean().optional(),
    showSources: z.boolean().optional(),
    minScore: z.number().min(0).max(1).optional(),
    tonePreset: z.nativeEnum(TonePreset).optional(),
    responseLengthPreset: z.nativeEnum(ResponseLengthPreset).optional(),
    hasWebSearch: z.boolean().optional(),
    webSearchMaxResults: z.number().int().nonnegative().optional(),
    webSearchUrls: websiteUrlLinesSchema,
    /**
     * Stream model-authored activity status lines ("Searching the knowledge
     * base…") to the widget while tools run.
     */
    showToolActivity: z.boolean().optional(),
    /**
     * Show the model's reasoning/thinking blocks above its answer in the
     * widget.
     */
    showReasoning: z.boolean().optional(),
    /**
     * When user-message moderation runs relative to the agent:
     * `parallel` (default) starts it alongside the model call and only
     * gates the first streamed answer token; `blocking` awaits the verdict
     * before the agent starts (v1 behavior); `off` skips moderation.
     */
    moderationMode: z.enum(['off', 'parallel', 'blocking']).optional(),
    /**
     * Additionally run the LLM vulgarity classifier (an extra smallModel
     * call per message) on top of the moderation API + static wordlist.
     */
    moderationLlmCheck: z.boolean().optional(),
    showFollowUpQuestions: z.boolean().optional(),
    language: z.string().optional(),
    fallbackLanguage: z.string().optional(),
    /**
     * Main-agent reasoning effort, honoured only for models listed in
     * `MODEL_REASONING_CAPABILITIES`. `null` (or unset) means "use the model
     * default"; `null` is what the admin form sends to clear an earlier
     * choice, because `ChatsService.update` merges `agentConfig` shallowly.
     */
    reasoningEffort: reasoningEffortSchema.nullable().optional(),
    newRagService: z.boolean().optional(),
    enabledBuiltinTools: builtinToolAllowlistSchema.optional(),
  })
  .strict();

export type AgentConfig = z.infer<typeof agentConfigSchema>;

export const legacyAgentConfigKeys = [
  'hybridAlpha',
  'retrievalCount',
  'retrievalTokenCap',
  'rerankCount',
  'weightRerankCoefficient',
  'rerankCoefficient',
  'minScore',
  'rewriteRetries',
  'historyLimit',
  'contextHistoryLimit',
] as const;

export function runtimeAgentConfig(config: AgentConfig): AgentConfig {
  const result = { ...config };
  for (const key of legacyAgentConfigKeys) {
    if (!agentConfigSchema.shape[key].safeParse(result[key]).success)
      delete result[key];
  }

  return result;
}

const compatibleAgentConfigSchema = agentConfigSchema.extend({
  hybridAlpha: z.number().optional(),
  retrievalCount: z.number().optional(),
  retrievalTokenCap: z.number().optional(),
  rerankCount: z.number().optional(),
  weightRerankCoefficient: z.number().optional(),
  rerankCoefficient: z.number().optional(),
  minScore: z.number().optional(),
  rewriteRetries: z.number().optional(),
  historyLimit: z.number().optional(),
  contextHistoryLimit: z.number().optional(),
});

export const voiceConfigSchema = z
  .object({
    keywords: z.array(z.string()).optional(),
    endpointing: z.number().min(10).optional(),
    silenceMessages: z.array(z.string()).optional(),
    silenceTimeout: z.number().min(10).optional(),
    /**
     * Hard wall-clock ceiling on a single call, in seconds. Unset means the
     * platform default (10 minutes).
     *
     * Not a nicety: per-turn Gemini cost *grows* with call length, because the
     * context window is re-billed on every turn — so spend on one call is
     * super-linear in its duration, and `contextWindowCompression` plus
     * proactive `goAway` reconnection means nothing else stops it.
     */
    maxCallSeconds: z.number().min(60).max(3600).optional(),
    /**
     * The Twilio number this chat answers on.
     *
     * Credentials are NOT here on purpose, even though a chat may now bring
     * its own Twilio account (optional; absent means the platform account in
     * `config('twilio')`). They live on `Chat.twilioAccountSid` /
     * `Chat.twilioAuthToken` instead — dedicated, `hidden: true` entity
     * columns, `authToken` encrypted at rest — set only via the dedicated
     * `chats.setTwilioCredentials` / `chats.clearTwilioCredentials`
     * mutations, never through this schema's generic create/update path.
     * See the doc comment on those fields in `Chat.ts` for why: a secret
     * living inside this jsonb blob would round-trip back to admin-app on
     * every read, since `chatsRouter`'s generic `findOne`/`update` return
     * the raw entity with no output redaction step. (History:
     * `Migration20260907010000` once stripped an `accountSid`/`authToken`
     * pair that earlier rows carried in the clear right here — don't repeat
     * that mistake by adding them back to this object.)
     */
    twilio: z
      .object({
        // Validate E.164 format, with whitespace allowed
        // number: z.string().regex(/^\+[1-9]\d{1,14}$/), // TODO fixme, form can't be saved with empty strings since it fails validation
        number: z.string(),
      })
      .nullish(),
    /**
     * Gemini Live S2S session configuration (voice rework v2, Phases 1/3/8).
     */
    gemini: z
      .object({
        voice: z.nativeEnum(GeminiVoices).optional(),
        /**
         * Live API `thinkingConfig.thinkingLevel`. Confirmed value set —
         * https://ai.google.dev/gemini-api/docs/live-api/capabilities.
         */
        thinkingLevel: z.enum(['minimal', 'low', 'medium', 'high']).optional(),
        /**
         * Live API `realtimeInputConfig.automaticActivityDetection.startOfSpeechSensitivity`.
         * Confirmed value set (the `StartSensitivity` enum in the
         * `@google/genai` SDK) — https://ai.google.dev/gemini-api/docs/live-api/capabilities.
         */
        startOfSpeechSensitivity: z
          .enum(['START_SENSITIVITY_LOW', 'START_SENSITIVITY_HIGH'])
          .optional(),
        /**
         * Live API `realtimeInputConfig.automaticActivityDetection.endOfSpeechSensitivity`.
         * Confirmed value set (the `EndSensitivity` enum in the
         * `@google/genai` SDK) — https://ai.google.dev/gemini-api/docs/live-api/capabilities.
         */
        endOfSpeechSensitivity: z
          .enum(['END_SENSITIVITY_LOW', 'END_SENSITIVITY_HIGH'])
          .optional(),
      })
      .optional(),
    /**
     * Operator hand-off from the voice agent to a human (voice rework v2,
     * Phase 8).
     */
    operatorTransfer: z
      .object({
        enabled: z.boolean(),
        /**
         * Cold (blind) transfer only — the agent drops off the call. Warm
         * (conference) transfer, where the agent stays briefed on the line,
         * is on the roadmap (plan §2.5) but not implemented; add `'warm'` to
         * this enum in the same change that implements it, not before.
         */
        mode: z.enum(['cold']),
        fallbackPhone: z.string().optional(),
        farewellMessage: z.string().optional(),
      })
      .optional(),
    /**
     * Live-session provider selector (post-MVP plan, Phase B2 — schema stub
     * only, nothing reads this yet). Deliberately `.optional()` rather than
     * `.default('geminiLive')`: a `.default()` would materialize the key on
     * every parse (including every already-persisted `voiceConfig` with no
     * opinion on provider), changing the inferred *output* shape and
     * surprising both the admin form (diffing/dirty-checking a field nobody
     * set) and the `Chat` entity's stored JSON. Absent means Gemini — the
     * only implemented provider — exactly as it does today. `openaiRealtime`
     * is Phase E and is not wired to anything; do not offer it in the admin
     * UI before then.
     */
    provider: z.enum(['geminiLive', 'openaiRealtime']).optional(),
    /**
     * Per-chat Live (speech-to-speech) model override, resolved by
     * `createLiveVoiceSession` → `GeminiLiveService`. `.optional()` rather
     * than `.default()` — same reasoning as `provider` above — so parsing an
     * already-persisted `voiceConfig` with no opinion doesn't materialize the
     * key. Absent means `DEFAULT_GEMINI_LIVE_MODEL`.
     */
    model: z.nativeEnum(GeminiLiveModel).optional(),
    /**
     * Twilio phone recording. Browser capture is deferred. Retention is
     * snapshotted at call start (30 days when omitted).
     */
    recording: z
      .object({
        enabled: z.boolean(),
        retentionDays: z.number().int().positive().max(3650).optional(),
      })
      .optional(),
    /**
     * Chatbot-embed voice entry mode (post-MVP plan, Phases C/F — not
     * implemented). Stub only: `'onDemand'` (mic/"Call" affordance in the
     * open widget, Phase C) and `'proactive'` (unsolicited incoming-call UI,
     * Phase F) are both unbuilt; this field is not read by any runtime path.
     */
    embed: z
      .object({
        mode: z.enum(['onDemand', 'proactive']).optional(),
      })
      .optional(),
  })
  .strict();

export type VoiceConfig = z.infer<typeof voiceConfigSchema>;

export const omnichannelConfigSchema = z
  .object({
    accessKey: z.string().nullish(),
    workspaceId: z.string().nullish(),
  })
  .strict();

export type OmnichannelConfig = z.infer<typeof omnichannelConfigSchema>;

export const chatDomainPolicySchema = z
  .object({
    mode: z.nativeEnum(ChatDomainPolicyMode).optional(),
    entries: websiteUrlLinesSchema,
  })
  .strict();

export type ChatDomainPolicy = z.infer<typeof chatDomainPolicySchema>;

export const chatbotConfigSchema = z
  .object({
    systemAvatar: z.string().optional(),
    initialMessage: z.string().optional(),
    bubbleTitle: z.string().optional(),
    bubbleDescription: z.string().optional(),
    requireAcknowledgement: z.boolean().optional(),
    disclaimerContent: z.string().optional(),
    showPopup: z.boolean().optional(),
    showPopupMobile: z.boolean().optional(),
    openOnLoad: z.boolean().optional(),
    displayMode: z.nativeEnum(ChatDisplayMode).optional(),
    showFeedbackButtons: z.boolean().optional(),
    showCallouts: z.boolean().optional(),
    /**
     * Play a short sound when a callout appears. Only relevant when
     * `showCallouts` is on. Missing means enabled.
     */
    calloutSoundEnabled: z.boolean().optional(),
    sourcesFormat: z.nativeEnum(SourcesFormat).optional(),
    sourcesDisplayMode: z.nativeEnum(SourcesDisplayMode).optional(),
    /**
     * Render `[cN]` markers as inline numeric superscripts in the answer.
     * Default off: provenance shows as the end-of-answer sources list and the
     * inline markers are stripped for clean prose. Turn on once page/anchor
     * precision is trustworthy enough for per-claim links.
     */
    inlineCitations: z.boolean().optional(),
    /**
     * Start the tool-activity trail expanded while the agent works (showing
     * each step's detail as it happens), folding to the summary line once the
     * answer streams. Default off: one calm line the user expands on demand.
     * Only takes effect when tool-activity display is enabled.
     */
    expandToolActivity: z.boolean().optional(),
    placement: z
      .object({
        position: z.nativeEnum(ChatPlacementPosition).optional(),
        offsetX: z.number().int().optional(),
        offsetY: z.number().int().optional(),
      })
      .optional(),
    captchaEnabled: z.boolean().optional(),
    captchaLimit: z.number().int().positive().optional(),
    captchaWindowSeconds: z.number().int().positive().optional(),
    captchaScoreThreshold: z.number().min(0).max(1).optional(),
    maxUserMessagesEnabled: z.boolean().optional(),
    maxUserMessagesLimit: z.number().int().positive().nullish(),
    domainPolicy: chatDomainPolicySchema.optional(),
  })
  .optional();

export type ChatbotConfig = z.infer<typeof chatbotConfigSchema>;

/**
 * Abuse protection applied to every new text chat unless the admin changes
 * it: invisible CAPTCHA after the second message and a weekly per-user
 * message cap.
 */
export const DEFAULT_CHAT_SECURITY_CONFIG = {
  captchaEnabled: true,
  captchaLimit: 2,
  captchaWindowSeconds: 1800,
  maxUserMessagesEnabled: true,
  maxUserMessagesLimit: 20,
} satisfies NonNullable<ChatbotConfig>;

export const chatSchema = baseOrganizationEntitySchema.extend({
  name: z.string().max(100),
  agent: z.nativeEnum(Agent).optional(),
  themeConfig: chatThemeConfigSchema.optional(),
  agentType: z.nativeEnum(AgentType),
  voiceConfig: voiceConfigSchema.optional(),
  omnichannelConfig: omnichannelConfigSchema.optional(),
  chatbotConfig: chatbotConfigSchema,
  agentConfig: agentConfigSchema,
  overrides: z.boolean().optional(),
  model: z.nativeEnum(ChatModel).optional(),
  smallModel: z.nativeEnum(ChatModel).optional(),
});

export const createChatSchema = chatSchema
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    organization: true,
  })
  .strict();

export const updateChatSchema = chatSchema
  .extend({ agentConfig: compatibleAgentConfigSchema })
  .partial()
  .required({
    id: true,
  })
  .omit({
    organization: true,
  })
  .strict();

export const exportXlsxSchema = z.object({
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
});

/**
 * Input for `chats.setTwilioCredentials` — a customer bringing their own
 * Twilio account for this chat. Both fields are required together: a REST
 * client needs the pair, and Twilio always presents them together in its
 * console, so there's no meaningful "change just one" case. Deliberately
 * separate from `updateChatSchema` — see the note on `voiceConfigSchema`'s
 * `twilio` field for why these must never flow through the generic
 * create/update path.
 */
export const setTwilioCredentialsSchema = z.object({
  id: z.string().uuid(),
  accountSid: z.string().min(1),
  authToken: z.string().min(1),
});

export type SetTwilioCredentialsDTO = z.infer<
  typeof setTwilioCredentialsSchema
>;

/**
 * Output for `chats.getTwilioCredentialsStatus` / `setTwilioCredentials` /
 * `clearTwilioCredentials`. Deliberately just a boolean — never the
 * `accountSid`, let alone the `authToken` — so the admin form can render
 * "custom Twilio account configured" without the value ever reaching the
 * browser once saved.
 */
export const twilioCredentialsStatusSchema = z.object({
  hasCustomCredentials: z.boolean(),
});

export type TwilioCredentialsStatus = z.infer<
  typeof twilioCredentialsStatusSchema
>;

export type ChatDTO = z.infer<typeof chatSchema>;
export type CreateChatDTO = z.infer<typeof createChatSchema>;
export type UpdateChatDTO = z.infer<typeof updateChatSchema>;

// Streamed chunk types
export const streamInitChunkSchema = z.object({
  type: z.literal('init'),
  id: z.string(),
});

export const streamTextChunkSchema = z.object({
  type: z.literal('text'),
  content: z.string(),
});

export const streamToolChunkSchema = z.object({
  type: z.literal('tool'),
  id: z.string(),
  index: z.number().int().nonnegative(),
  name: z.string(),
  args: z.string(),
});

export const streamChunkSchema = z.union([
  streamInitChunkSchema,
  streamTextChunkSchema,
  streamToolChunkSchema,
]);

export type StreamInitChunk = z.infer<typeof streamInitChunkSchema>;
export type StreamTextChunk = z.infer<typeof streamTextChunkSchema>;
export type StreamToolChunk = z.infer<typeof streamToolChunkSchema>;
export type StreamChunk = z.infer<typeof streamChunkSchema>;
